import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Hospital, Home as HomeIcon, Building2, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Settings() {
  const { user, setMode, logout, refreshMe } = useAuth();
  const { t } = useTranslation();
  const [trust, setTrust] = useState(user?.trust || "");
  const [savingTrust, setSavingTrust] = useState(false);
  const [consent, setConsent] = useState(!!user?.research_consent);
  const [savingConsent, setSavingConsent] = useState(false);

  const change = async (mode) => {
    try { await setMode(mode); toast.success(`Switched to ${mode} mode`); } catch { toast.error("Could not change mode"); }
  };

  const saveTrust = async () => {
    setSavingTrust(true);
    try { await api.patch("/auth/trust", { trust }); await refreshMe(); toast.success("NHS Trust saved"); }
    catch { toast.error("Could not save"); }
    finally { setSavingTrust(false); }
  };

  const toggleConsent = async (val) => {
    setSavingConsent(true);
    try {
      await api.patch("/auth/research-consent", { consent: val });
      setConsent(val);
      await refreshMe();
      toast.success(val ? "Thank you — your anonymised data may now contribute to research" : "Research consent withdrawn");
    } catch { toast.error("Could not save"); }
    finally { setSavingConsent(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="settings-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">{t("settings.title")}</h1>
        <p className="text-slate-600 mt-2 max-w-xl">{t("settings.intro")}</p>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">{t("settings.mode_title")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("settings.mode_desc")}</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button data-testid="mode-community" onClick={() => change("community")} className={`rounded-2xl border p-4 text-left ${user?.mode === "community" ? "border-teal-700 bg-teal-50" : "border-slate-200"}`}>
            <HomeIcon className="w-5 h-5 text-teal-700 mb-2" />
            <div className="font-semibold text-slate-900">{t("settings.mode_community")}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t("settings.mode_community_desc")}</div>
          </button>
          <button data-testid="mode-inpatient" onClick={() => change("inpatient")} className={`rounded-2xl border p-4 text-left ${user?.mode === "inpatient" ? "border-teal-700 bg-teal-50" : "border-slate-200"}`}>
            <Hospital className="w-5 h-5 text-teal-700 mb-2" />
            <div className="font-semibold text-slate-900">{t("settings.mode_inpatient")}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t("settings.mode_inpatient_desc")}</div>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="trust-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold text-slate-900">{t("settings.trust_title")}</h2>
            <p className="text-sm text-slate-500 mt-1">{t("settings.trust_desc")}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <input
                data-testid="trust-input"
                value={trust}
                onChange={(e) => setTrust(e.target.value)}
                placeholder={t("settings.trust_placeholder")}
                className="flex-1 min-w-[200px] border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              />
              <button data-testid="trust-save" onClick={saveTrust} disabled={savingTrust} className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-1">
                <Save className="w-4 h-4" /> {t("settings.trust_save")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="language-card">
        <h2 className="font-display text-lg font-bold text-slate-900">{t("settings.language_title")}</h2>
        <p className="text-sm text-slate-500 mt-1 mb-3">{t("settings.language_desc")}</p>
        <LanguageSwitcher />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="research-consent-card">
        <h2 className="font-display text-lg font-bold text-slate-900">Contribute to research (anonymised)</h2>
        <p className="text-sm text-slate-500 mt-1">Allow your fully anonymised, aggregated data (mood, distress, plan-engagement) to be included in Three Spheres Healthcare's PhD research. No identifiable information ever leaves your account. You can withdraw at any time.</p>
        <label className="mt-3 inline-flex items-center gap-3 cursor-pointer">
          <input
            data-testid="research-consent-toggle"
            type="checkbox"
            checked={consent}
            disabled={savingConsent}
            onChange={(e) => toggleConsent(e.target.checked)}
            className="w-5 h-5 rounded text-teal-700 focus:ring-teal-500"
          />
          <span className="text-sm font-medium text-slate-800">{consent ? "Consent granted" : "Tap to opt in"}</span>
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">{t("settings.account_title")}</h2>
        <div className="mt-2 text-sm text-slate-600">{user?.name} · {user?.email}</div>
        <button data-testid="settings-logout" onClick={logout} className="mt-4 border border-slate-300 hover:bg-slate-50 rounded-xl px-4 py-2 text-sm font-semibold text-slate-700">
          {t("nav.sign_out")}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        {t("app.disclaimer")}
      </div>
    </div>
  );
}
